// Shared timezone option list used by the admin timezone screens.
// Mirrors the Standard/Daylight lists historically embedded in
// AccountPatientsTimezones.js, but exposes them with the fields the device
// LocationInfo.TimeZone payload needs (Code + Name + GmtOffset).
// Timezone change reference: https://www.timeanddate.com/time/change/usa

export interface TimezoneOption {
  // GmtOffset as a signed string, e.g. "-5", "+1" — used as the <option> value.
  valueString: string;
  // Human-readable description shown in the dropdown.
  name: string;
  // AccuWeather-style abbreviation stored as TimeZone.Code, e.g. "EST", "EDT".
  shortName: string;
}

export const StandardTimezones: TimezoneOption[] = [
  { valueString: '+1', name: '(GMT+01) Central European Time - Stockholm', shortName: 'CET' },
  { valueString: '+2', name: '(GMT+02) Israel Standard Time - Jerusalem', shortName: 'IST' },
  { valueString: '-5', name: '(GMT-05) Eastern Standard Time - New York', shortName: 'EST' },
  { valueString: '-6', name: '(GMT-06) Central Standard Time - Chicago', shortName: 'CST' },
  { valueString: '-7', name: '(GMT-07) Mountain Standard Time - Salt Lake City', shortName: 'MST' },
  { valueString: '-8', name: '(GMT-08) Pacific Standard Time - Los Angeles', shortName: 'PST' },
  { valueString: '-9', name: '(GMT-09) Alaska Standard Time - Anchorage', shortName: 'AKST' },
  { valueString: '-10', name: '(GMT-10) Hawaii Standard Time - Honolulu', shortName: 'HST' },
];

export const DaylightTimezones: TimezoneOption[] = [
  { valueString: '+2', name: '(GMT+02) Central European Summer Time - Stockholm', shortName: 'CEST' },
  { valueString: '+3', name: '(GMT+03) Israel Daylight Time - Jerusalem', shortName: 'IDT' },
  { valueString: '-4', name: '(GMT-04) Eastern Daylight Time - New York', shortName: 'EDT' },
  { valueString: '-5', name: '(GMT-05) Central Daylight Time - Chicago', shortName: 'CDT' },
  { valueString: '-6', name: '(GMT-06) Mountain Daylight Time - Salt Lake City', shortName: 'MDT' },
  { valueString: '-7', name: '(GMT-07) Pacific Daylight Time - Los Angeles', shortName: 'PDT' },
  { valueString: '-8', name: '(GMT-08) Alaska Daylight Time - Anchorage', shortName: 'AKDT' },
  { valueString: '-9', name: '(GMT-09) Hawaii Daylight Time - Honolulu', shortName: 'HDT' },
];

// US daylight saving runs from the 2nd Sunday of March to the 1st Sunday of
// November. https://gist.github.com/danalloway/17b48fddab9028432c68
export function isUsDaylightSaving(date: Date = new Date()): boolean {
  const year = date.getFullYear();

  const firstOfMarch = new Date(year, 2, 1);
  const daysUntilFirstSundayInMarch = (7 - firstOfMarch.getDay()) % 7;
  const secondSundayInMarch = firstOfMarch.getDate() + daysUntilFirstSundayInMarch + 7;
  const dstStart = new Date(year, 2, secondSundayInMarch);

  const firstOfNovember = new Date(year, 10, 1);
  const daysUntilFirstSundayInNov = (7 - firstOfNovember.getDay()) % 7;
  const firstSundayInNovember = firstOfNovember.getDate() + daysUntilFirstSundayInNov;
  const dstEnd = new Date(year, 10, firstSundayInNovember);

  return date >= dstStart && date <= dstEnd;
}

// Returns the timezone option list relevant for the given date (DST-aware).
export function getActiveTimezoneOptions(date: Date = new Date()): TimezoneOption[] {
  return isUsDaylightSaving(date) ? DaylightTimezones : StandardTimezones;
}

// Look up the option for a signed-offset value within the active list.
export function findTimezoneOption(valueString: string, date: Date = new Date()): TimezoneOption | undefined {
  return getActiveTimezoneOptions(date).find((tz) => tz.valueString === valueString);
}
